import React, { useCallback, useRef } from 'react';
import { HOLOLAB_COLORS, HOLOLAB_FONTS } from '../../constants';

interface SimulationControlsProps {
  isPaused: boolean;
  playbackSpeed: number;
  experimentTime: number;
  maxTime: number;
  isReacting: boolean;
  experimentComplete: boolean;
  onPauseResume: () => void;
  onSpeedChange: (speed: number) => void;
  onSeek: (time: number) => void;
}

const SPEED_OPTIONS = [0.25, 0.5, 1, 2];

const formatTime = (minutes: number): string => {
  const totalSeconds = Math.round(minutes * 60);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const SimulationControls: React.FC<SimulationControlsProps> = ({
  isPaused,
  playbackSpeed,
  experimentTime,
  maxTime,
  isReacting,
  experimentComplete,
  onPauseResume,
  onSpeedChange,
  onSeek,
}) => {
  const wasPlayingBeforeDrag = useRef(false);
  const isDragging = useRef(false);

  const handleSliderMouseDown = useCallback(() => {
    isDragging.current = true;
    if (isReacting && !isPaused) {
      wasPlayingBeforeDrag.current = true;
      onPauseResume(); // auto-pause
    } else {
      wasPlayingBeforeDrag.current = false;
    }
  }, [isReacting, isPaused, onPauseResume]);

  const handleSliderMouseUp = useCallback(() => {
    isDragging.current = false;
    if (wasPlayingBeforeDrag.current) {
      wasPlayingBeforeDrag.current = false;
      onPauseResume(); // resume
    }
  }, [onPauseResume]);

  const handleSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSeek(parseFloat(e.target.value));
    },
    [onSeek]
  );

  const canToggle = isReacting || experimentComplete || experimentTime > 0;
  const progressPercent = (experimentTime / maxTime) * 100;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: '52px',
        left: '14px',
        right: '14px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 14px',
        borderRadius: '10px',
        background: 'rgba(255,255,255,0.82)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        border: '1px solid rgba(10,25,41,0.08)',
        boxShadow: '0 2px 10px rgba(10,25,41,0.08)',
        zIndex: 5,
      }}
    >
      {/* Play / Pause button */}
      <button
        onClick={onPauseResume}
        disabled={!canToggle}
        aria-label={isPaused || experimentComplete ? 'Play' : 'Pause'}
        style={{
          width: '30px',
          height: '30px',
          borderRadius: '50%',
          border: `1.5px solid ${canToggle ? HOLOLAB_COLORS.precisionBlue : 'rgba(10,25,41,0.12)'}`,
          background: canToggle ? 'rgba(46,92,138,0.06)' : 'transparent',
          color: canToggle ? HOLOLAB_COLORS.precisionBlue : 'rgba(10,25,41,0.25)',
          cursor: canToggle ? 'pointer' : 'default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'all 150ms',
          padding: 0,
        }}
      >
        {isPaused || experimentComplete || !isReacting ? (
          // Play icon
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        ) : (
          // Pause icon
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 4h4v16H6zM14 4h4v16h-4z" />
          </svg>
        )}
      </button>

      {/* Speed pills */}
      <div style={{ display: 'flex', gap: '3px', flexShrink: 0 }}>
        {SPEED_OPTIONS.map((speed) => {
          const isActive = playbackSpeed === speed;
          return (
            <button
              key={speed}
              onClick={() => onSpeedChange(speed)}
              style={{
                padding: '3px 7px',
                borderRadius: '6px',
                border: `1px solid ${isActive ? HOLOLAB_COLORS.precisionBlue : 'rgba(10,25,41,0.1)'}`,
                background: isActive ? 'rgba(46,92,138,0.12)' : 'transparent',
                fontFamily: HOLOLAB_FONTS.mono,
                fontSize: '10px',
                fontWeight: isActive ? 600 : 400,
                color: isActive ? HOLOLAB_COLORS.precisionBlue : 'rgba(10,25,41,0.45)',
                cursor: 'pointer',
                transition: 'all 150ms',
                lineHeight: 1.2,
              }}
            >
              {speed}x
            </button>
          );
        })}
      </div>

      {/* Seek slider */}
      <div style={{ flex: 1, position: 'relative', minWidth: '60px', height: '20px', display: 'flex', alignItems: 'center' }}>
        {/* Track background */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            height: '3px',
            borderRadius: '2px',
            background: 'rgba(10,25,41,0.08)',
            pointerEvents: 'none',
          }}
        />
        {/* Track fill */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            width: `${progressPercent}%`,
            height: '3px',
            borderRadius: '2px',
            background: HOLOLAB_COLORS.precisionBlue,
            pointerEvents: 'none',
            transition: isDragging.current ? 'none' : 'width 100ms linear',
          }}
        />
        <input
          type="range"
          min={0}
          max={maxTime}
          step={0.01}
          value={experimentTime}
          onChange={handleSliderChange}
          onMouseDown={handleSliderMouseDown}
          onMouseUp={handleSliderMouseUp}
          onTouchStart={handleSliderMouseDown}
          onTouchEnd={handleSliderMouseUp}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            width: '100%',
            height: '20px',
            margin: 0,
            appearance: 'none',
            WebkitAppearance: 'none',
            background: 'transparent',
            cursor: 'pointer',
            zIndex: 1,
          }}
        />
      </div>

      {/* Time display */}
      <span
        style={{
          fontFamily: HOLOLAB_FONTS.mono,
          fontSize: '11px',
          fontWeight: 500,
          color: 'rgba(10,25,41,0.55)',
          whiteSpace: 'nowrap',
          flexShrink: 0,
          minWidth: '70px',
          textAlign: 'right',
        }}
      >
        {formatTime(experimentTime)} / {formatTime(maxTime)}
      </span>

      {/* Slider thumb styling */}
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: ${HOLOLAB_COLORS.precisionBlue};
          border: 2px solid #fff;
          box-shadow: 0 1px 4px rgba(10,25,41,0.18);
          cursor: pointer;
          transition: transform 150ms;
        }
        input[type="range"]::-webkit-slider-thumb:hover {
          transform: scale(1.2);
        }
        input[type="range"]::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: ${HOLOLAB_COLORS.precisionBlue};
          border: 2px solid #fff;
          box-shadow: 0 1px 4px rgba(10,25,41,0.18);
          cursor: pointer;
        }
        input[type="range"]::-webkit-slider-runnable-track {
          height: 3px;
          background: transparent;
        }
        input[type="range"]::-moz-range-track {
          height: 3px;
          background: transparent;
          border: none;
        }
      `}</style>
    </div>
  );
};

export default SimulationControls;
